import { Task } from "@/bryntum-lib/models/Task";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { cn } from "@/modern/utils";
import { uploadFile } from "@/network/network.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";
import { REACT_EDITOR_CONTEXT_MENU_KEYS, SWIVU_ASSIST_CONFIG } from "@/other/constants/OtherKeys.jsx";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils.jsx";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { useSwivuForm } from "@/react/hooks/useSwivuForm.jsx";
import type { LucideIcon } from "lucide-react";
import { CheckCircle2, Circle, Info } from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Controller } from "react-hook-form";
import { toast } from "sonner";
import { BaseModal } from "../../base/BaseModal.jsx";
import { BaseModalBody } from "../../base/BaseModalBody.jsx";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog";
import SpotIncentiveImagePreview, { ImageThumbnail } from "./SpotIncentiveImagePreview";
import SpotIncentivePointsAssignment from "./SpotIncentivePointsAssignment";
import SpotIncentiveResourceSelector from "./SpotIncentiveResourceSelector";
import { SpotIncentiveFormData, spotIncentiveSchema } from "./spotIncentiveSchema";
import SpotIncentiveTaskSelector from "./SpotIncentiveTaskSelector";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";

// ===== TYPE DEFINITIONS =====
interface SpotIncentiveFileData {
  fileId?: number;
  documentId?: number;
  file?: File;
  fileName?: string;
}

interface SpotIncentiveProps {
  record: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  onClose: () => void;
  editorInstance: unknown;
  selectedTask?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  initialFileId?: number;
  initialFileData?: SpotIncentiveFileData;
  isCreatingUnreleasedRecord?: boolean;
  onReviewClick?: (newFile?: BoardFile) => void;
  initialName?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  initialResourceIds?: number[];
}

interface FormSectionProps {
  title: string;
  step?: number;
  description?: string;
  optional?: boolean;
  completed?: boolean;
  dimmed?: boolean;
  showPending?: boolean;
  dependencyMessage?: string;
  showDependencyMessage?: boolean;
  children: ReactNode;
}

const FormSection = ({ step, title, description, optional, completed, dimmed, showPending = false, dependencyMessage, showDependencyMessage = false, children }: FormSectionProps) => (
  <section className={cn("rounded-xl border border-border p-5 shadow-sm transition-colors bg-card", dimmed && "border-dashed bg-muted/30 opacity-80")}>
    <div className="flex items-start gap-3">
      {step && (
        <div className="flex size-10 items-center justify-center rounded-xl border border-border/80 bg-muted/40 text-muted-foreground">
          <div>{step}</div>
        </div>
      )}
      <div className="flex-1 space-y-1">
        <p className="text-sm font-semibold text-muted-foreground flex items-center gap-3">
          <span>{title}</span>
        </p>
        {description && <p className="text-xs text-muted-foreground/80">{description}</p>}
      </div>
      {(completed || showPending) && (() => {
        const { Icon, label, colorClass } = completed
          ? { Icon: CheckCircle2, label: "Complete", colorClass: "text-success" }
          : { Icon: Circle, label: "Pending", colorClass: "text-muted-foreground/60" };
        return (
          <span className={cn("flex items-center gap-1 text-xs font-semibold mr-1", colorClass)}>
            <Icon className="size-4" />
            {label}
          </span>
        );
      })()}
      {optional && <span className="text-xs font-semibold mr-1 text-muted-foreground/60">Optional</span>}
    </div>
    <div className={cn("mt-4 space-y-3")}>{children}</div>
    {showDependencyMessage && dependencyMessage && <DependencyNotice icon={Info} message={dependencyMessage} />}
  </section>
);

const DependencyNotice = ({ icon: Icon, message }: { icon: LucideIcon; message: string }) => (
  <div className="inline-flex items-center gap-2 rounded-lg border bg-muted/50 border-border px-3 py-2 text-xs font-medium text-muted-foreground shadow-sm mt-4">
    <Icon className="size-4 text-muted-foreground/70" />
    <span>{message}</span>
  </div>
);

const selectionTriggerBaseClass =
  "hover:bg-muted/30 hover:border-foreground/50 w-full justify-between rounded-xl border border-border/70 bg-background min-h-12 p-4 text-left shadow-sm transition-colors  focus-visible:ring-1 focus-visible:ring-primary/40 focus-visible:outline-none h-auto";

// ===== COMPONENT =====
const SpotIncentive = ({ record, onClose, editorInstance, selectedTask, initialFileId, initialFileData, isCreatingUnreleasedRecord, onReviewClick, initialName, initialStartDate, initialEndDate, initialResourceIds }: SpotIncentiveProps) => {
  const { project } = useContext(BryntumContext) as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  // Permission checking for context menu options
  const memoizedPermissionMap = useMemo(() => {
    const permissionKeys = [INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS, INCENTIVE_PERMISSION_KEYS.POINT_DELETERS];
    return permissionKeys.reduce((acc: Record<string, boolean>, permissionKey) => {
      acc[permissionKey] = checkIncentivePermission({ project, permissionKey });
      return acc;
    }, {});
  }, [project]);

  // Get documentId from initialFileId or initialFileData.fileId if available
  const fileIdToCheck = initialFileId || initialFileData?.fileId;
  const initialBoardFile = fileIdToCheck ? project?.boardFilesStore?.getById(fileIdToCheck) : null;
  const initialDocumentId = initialFileData?.documentId || initialBoardFile?.documentId || null;
  const initialFileName = initialFileData?.fileName || initialBoardFile?.name || undefined;

  const { form, formState } = useSwivuForm({
    schema: spotIncentiveSchema,
    record: record, // Empty record for new spot incentive
    updateRecordOnFormValueChange: false,
    version: "",
    defaultValues: {
      name: initialName || "",
      taskId: selectedTask?.id || null,
      resourceIds: initialResourceIds || [],
      startDate: initialStartDate || (() => {
        const date = new Date();
        date.setDate(date.getDate() - 5);
        return date.toISOString();
      })(),
      endDate: initialEndDate || new Date().toISOString(),
      positiveTagIds: [],
      negativeTagIds: [],
      multiplyByDays: false,
      fileId: initialFileId || null,
      uploadedDocumentId: initialDocumentId,
      uploadedFileName: initialFileName
    }
  });

  const [showTaskSelector, setShowTaskSelector] = useState(false);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [showPointsAssignment, setShowPointsAssignment] = useState(false);
  const [showInactiveDialog, setShowInactiveDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [createdSpotIncentiveId, setCreatedSpotIncentiveId] = useState<number | null>(null);
  const uploadedFileRecord = useRef<BoardFile | undefined>(undefined);

  useEffect(() => {
    if (selectedTask?.id) {
      form.setValue("taskId", selectedTask.id, { shouldValidate: false, shouldDirty: false });
    }
  }, []);

  // Create and cleanup uploaded file object URL
  const uploadedFileUrl = useMemo(() => {
    if (uploadedFile) {
      return URL.createObjectURL(uploadedFile);
    }
    return null;
  }, [uploadedFile]);

  useEffect(() => {
    return () => {
      if (uploadedFileUrl) {
        URL.revokeObjectURL(uploadedFileUrl);
      }
    };
  }, [uploadedFileUrl]);

  const tasks = useMemo(
    () =>
      project?.extraTaskStore?.query((record: Task) => {
        const selectedBoard = getSavedBoardSelection();
        const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
        return !record.isPhantom && (canShowArchivedRecords || !record.archived);
      }, true) || [],
    [project]
  );

  const getSelectedTaskName = (taskId: number | null) => {
    if (!taskId) return "Choose a task to assign points...";
    const task = tasks.find((t: any) => t.id === taskId); // eslint-disable-line @typescript-eslint/no-explicit-any
    return task?.name || "Task not found";
  };

  const getSelectedResourceNames = (resourceIds: number[]) => {
    if (resourceIds.length === 0) return ["No resources selected"];
    if (!project?.resourceStore) return resourceIds.map((id) => `Resource ${id}`);

    return resourceIds.map((id) => {
      const resource = project.resourceStore.getById(id);
      return resource?.name || `Resource ${id}`;
    });
  };

  const getDateRangeDisplay = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return ` • ${start} - ${end}`;
  };

  const getTotalPoints = (positiveTagIds: number[], negativeTagIds: number[]): number => {
    let total = 0;

    // Calculate positive points
    positiveTagIds.forEach((tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      if (tag && tag.categoryRecord) {
        total += tag.categoryRecord.points;
      }
    });

    // Calculate negative points
    negativeTagIds.forEach((tagId: number) => {
      const tag = project?.incentiveTagsStore?.getById(tagId);
      if (tag && tag.categoryRecord) {
        total -= tag.categoryRecord.points;
      }
    });

    return total;
  };

  const name = form.watch("name");
  const taskId = form.watch("taskId");
  const resourceIds = form.watch("resourceIds");
  const positiveTagIds = form.watch("positiveTagIds");
  const negativeTagIds = form.watch("negativeTagIds");
  const startDate = form.watch("startDate");
  const endDate = form.watch("endDate");
  const multiplyByDays = form.watch("multiplyByDays");
  const uploadedDocumentId = form.watch("uploadedDocumentId");
  const uploadedFileName = form.watch("uploadedFileName");
  const fileId = form.watch("fileId");
  const hasPointsAssigned = positiveTagIds.length > 0 || negativeTagIds.length > 0;
  const isResourceSelectionLocked = !taskId;
  const isPointsAssignmentLocked = !taskId || resourceIds.length === 0;
  const totalPoints = getTotalPoints(positiveTagIds, negativeTagIds);
  const dateRangeDisplay = getDateRangeDisplay(startDate, endDate);

  // Check if we should show warning for no resources found on uploaded date
  const shouldShowNoResourcesWarning = isCreatingUnreleasedRecord && resourceIds.length === 0 && taskId && !isResourceSelectionLocked;
  const getUploadedDateDisplay = () => {
    if (startDate) {
      return new Date(startDate).toLocaleDateString();
    }
    return "";
  };
  // Get effective documentId for AI Adjustment
  // Ensure we pass the documentId if it exists (could be number or 0)
  const effectiveDocumentId = typeof uploadedDocumentId === 'number' ? uploadedDocumentId : undefined;
  const trimmedName = (name || "").trim();
  const completionState: Record<string, boolean> = {
    name: Boolean(trimmedName),
    task: Boolean(taskId),
    resources: resourceIds.length > 0,
    points: hasPointsAssigned
  };
  const Steps = ["name", "task", "resources", "points"];

  const handleFileUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }

    setIsUploading(true);
    try {
      const { data } = await uploadFile(file, file.name);
      form.setValue("uploadedDocumentId", data.id);
      form.setValue("uploadedFileName", file.name);
      setUploadedFile(file); // Store file for preview
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }, [form]);

  // Handle initialFileData File object from CompanyCam - upload it immediately
  useEffect(() => {
    if (initialFileData?.file) {
      handleFileUpload(initialFileData.file);
    }
  }, [initialFileData, handleFileUpload]);

  const showSuccessToast = () => {
    toast.success("Points adjusted are now visible on the Points surface.");
  }

  const handleSave = async (data: SpotIncentiveFormData) => {
    setIsSubmitting(true);

    try {
      const selectedBoard = getSavedBoardSelection();
      let finalFileId = data.fileId;

      // If user uploaded a new file, create BoardFile record first
      if (data.uploadedDocumentId && !finalFileId) {
        const [newBoardFile] = project.boardFilesStore.add({
          documentId: data.uploadedDocumentId,
          taskId: data.taskId,
          name: data.uploadedFileName
        });
        await project.sync();
        finalFileId = newBoardFile.id;
        uploadedFileRecord.current = newBoardFile;
      }

      // Create SpotIncentive record directly in store
      const [newSpotIncentive] = project.spotIncentiveStore.add({
        name: data.name,
        taskId: data.taskId,
        resourceIds: data.resourceIds,
        positiveTagIds: data.positiveTagIds,
        negativeTagIds: data.negativeTagIds,
        startDate: data.startDate,
        endDate: data.endDate,
        boardId: selectedBoard.id,
        multiplyByDays: data.multiplyByDays,
        fileId: finalFileId || null,
        isReleased: !isCreatingUnreleasedRecord
      });

      // If creating unreleased record, show dialog instead of closing
      if (isCreatingUnreleasedRecord) {
        await project.sync();
        setCreatedSpotIncentiveId(newSpotIncentive.id);
        setShowInactiveDialog(true);
      } else {
        showSuccessToast();
        onClose();
      }
    } catch (_error) {
      toast.error("Failed to create spot point", {
        description: "Please try again or contact support if the issue persists."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const MODAL_SIZE_CONFIG = {
    width: "auto",
    height: "min(90vh, 47rem)",
    minWidth: "min(90vw, 48rem)",
    minHeight: "30rem",
    maxWidth: "95vw",
    maxHeight: "95vh"
  };

  return (
    <>
      <BaseModal
        title="Spot Points Adjustments"
        description="Award or deduct points for resources against tasks"
        onClose={onClose}
        size={MODAL_SIZE_CONFIG}
        editorInstance={editorInstance}
        record={record}
        headerIconCls=""
        modal={false}
        version=""
        className="border-border/40 bg-popover shadow-[0px_18px_60px_rgba(15,23,42,0.18)]"
        headerClassName="!px-6 !py-4 bg-transparent"
        contextMenuConfig={{
          [REACT_EDITOR_CONTEXT_MENU_KEYS.ARCHIVE]: {
            disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS],
            hidden: record.isPhantom
          },
          [REACT_EDITOR_CONTEXT_MENU_KEYS.UNARCHIVE]: {
            disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS],
            hidden: record.isPhantom
          },
          [REACT_EDITOR_CONTEXT_MENU_KEYS.DELETE]: {
            disabled: !memoizedPermissionMap[INCENTIVE_PERMISSION_KEYS.POINT_DELETERS],
            hidden: record.isPhantom
          }
        }}
        swivuAssistanceConfig={SWIVU_ASSIST_CONFIG.spotIncentive}
        footerRenderer={() => (
          <Button
            variant="default"
            size="default"
            className="px-6 text-sm font-semibold"
            onClick={form.handleSubmit(handleSave)}
            disabled={!name || !taskId || resourceIds.length === 0 || isSubmitting}
          >
            {isSubmitting ? "Creating..." : isCreatingUnreleasedRecord ? "Save" : "Adjust Points"}
          </Button>
        )}
      >
        <BaseModalBody className="space-y-6 !p-0 sm:!p-6 bg-none" contentElementClassName="bg-background">
          <div className="space-y-5 ">
            <div className="absolute bg-popover left-0 top-0 w-full z-10 flex flex-col gap-1  border border-border/60  px-6 py-3 text-sm shadow-inner sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-muted-foreground">Complete the following 4 steps</p>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground/80 text-xs font-medium">
                {Steps.map((key, index) => (
                  <span
                    key={key}
                    className={cn(
                      "flex size-6 items-center justify-center rounded-full border text-[11px]",
                      completionState[key] ? "border-success/50 bg-success/10 text-success font-semibold" : "border-border/70 bg-background"
                    )}
                  >
                    {completionState[key] ? <CheckCircle2 className="size-3.5" /> : index + 1}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-6 pt-8">
              <FormSection step={1} title="Name" description="Give this adjustment a clear label so teams quickly understand it." completed={completionState.name} showPending>
                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <>
                      <Input
                        autoFocus
                        id="incentive-name"
                        autoComplete="off"
                        placeholder="e.g. Punch list bonus"
                        className="h-12 rounded-xl border-border bg-background text-sm placeholder:text-muted-foreground/30"
                        {...field}
                      />
                      {formState.errors.name && <p className="text-xs text-destructive">{String(formState.errors.name.message)}</p>}
                    </>
                  )}
                />
              </FormSection>

              <FormSection step={2} title="Task Selection" description="Tie this point to the work it reflects." completed={completionState.task} showPending>
                <Button
                  variant="outline"
                  className={cn(
                    selectionTriggerBaseClass,
                    !taskId && "text-muted-foreground",
                    isCreatingUnreleasedRecord && "cursor-default opacity-100"
                  )}
                  onClick={() => {
                    if (!isCreatingUnreleasedRecord) {
                      setShowTaskSelector(true);
                    }
                  }}
                  disabled={isCreatingUnreleasedRecord}
                >
                  <div className="text-left">
                    <p className={cn("text-sm font-semibold", !taskId && "font-normal text-muted-foreground")}>{getSelectedTaskName(taskId)}</p>
                  </div>
                  {!isCreatingUnreleasedRecord && (
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{taskId ? "Change" : "Select"}</span>
                  )}
                </Button>
                {formState.errors.taskId && <p className="text-xs text-destructive">{String(formState.errors.taskId.message)}</p>}
              </FormSection>

              <FormSection
                step={3}
                title="Resource Selection"
                description="Select the resources affected during the time range."
                completed={completionState.resources}
                dimmed={isResourceSelectionLocked || shouldShowNoResourcesWarning}
                dependencyMessage={
                  isResourceSelectionLocked
                    ? "Pick a task in step 2 before choosing resources."
                    : shouldShowNoResourcesWarning
                      ? `No resources found on the uploaded date of the image${getUploadedDateDisplay() ? ` (${getUploadedDateDisplay()})` : ""}.`
                      : undefined
                }
                showDependencyMessage={isResourceSelectionLocked || shouldShowNoResourcesWarning}
                showPending
              >
                <div className={cn("space-y-2 rounded-lg border border-transparent", isResourceSelectionLocked && "border-dashed border-border/70 bg-transparent")}>
                  <Button
                    variant="outline"
                    className={cn(selectionTriggerBaseClass, "w-full justify-start", resourceIds.length > 0 && "!min-h-[3.5rem]", isResourceSelectionLocked && "cursor-not-allowed opacity-60")}
                    onClick={() => {
                      if (!isResourceSelectionLocked) {
                        setShowResourceSelector(true);
                      }
                    }}
                    disabled={isResourceSelectionLocked}
                  >
                    <div className="flex-1 text-left">
                      {resourceIds.length > 0 ? (
                        <>
                          <p className="text-sm font-semibold">
                            {getSelectedResourceNames(resourceIds).slice(0, 2).join(", ")}
                            {resourceIds.length > 2 && ` +${resourceIds.length - 2} more`}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {resourceIds.length} resource{resourceIds.length !== 1 ? "s" : ""} selected
                            {dateRangeDisplay && <span className="ml-1">{dateRangeDisplay}</span>}
                            {multiplyByDays && <span className="ml-1">• Multiplying points by days-on-task</span>}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">{taskId ? "Select resources to continue." : "Select a task first, then choose resources."}</p>
                      )}
                    </div>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">{resourceIds.length > 0 ? "Edit" : "Select"}</span>
                  </Button>
                  {formState.errors.resourceIds && <p className="text-xs text-destructive">{String(formState.errors.resourceIds.message)}</p>}
                </div>
              </FormSection>

              <FormSection
                title="Evidence Photo"
                description="Upload an image to enable AI-assisted point assignment"
                optional
              >
                <div className="flex items-center gap-3">
                  {/* Display file info if uploaded or pre-existing */}
                  {(fileId || uploadedDocumentId) ? (() => {
                    const fileRecord = fileId ? project?.boardFilesStore?.getById(fileId) : null;
                    const imageUrl = fileRecord?.url;
                    const thumbnailUrl = fileRecord?.thumbnailUrl;

                    // For existing files with URL, show preview from URL
                    if (fileId && imageUrl) {
                      return (
                        <SpotIncentiveImagePreview
                          images={[{
                            src: imageUrl,
                            alt: uploadedFileName || fileRecord?.name || 'Evidence image',
                            caption: fileRecord?.description
                          }]}
                          allowZoom={true}
                        >
                          <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-3 flex-1 cursor-pointer hover:bg-muted/30 transition-colors">
                            <ImageThumbnail
                              src={thumbnailUrl || imageUrl}
                              alt={uploadedFileName || fileRecord?.name || 'Evidence image'}
                              index={0}
                              className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {uploadedFileName || fileRecord?.name || "Evidence image"}
                              </p>
                              <span className="text-xs text-muted-foreground">Click to preview</span>
                            </div>
                          </div>
                        </SpotIncentiveImagePreview>
                      );
                    }

                    // For newly uploaded files, show preview from File object
                    if (uploadedFile && uploadedFileUrl) {
                      return (
                        <SpotIncentiveImagePreview
                          images={[{
                            src: uploadedFileUrl,
                            alt: uploadedFileName || "Evidence image",
                            caption: `${uploadedFile.name} (${(uploadedFile.size / 1024 / 1024).toFixed(2)}MB)`
                          }]}
                          allowZoom={true}
                        >
                          <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-3 flex-1 cursor-pointer hover:bg-muted/30 transition-colors">
                            <ImageThumbnail
                              src={uploadedFileUrl}
                              alt={uploadedFileName || "Evidence image"}
                              index={0}
                              className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {uploadedFileName || "Evidence image"}
                              </p>
                              <p className="text-xs text-muted-foreground">Click to preview</p>
                            </div>
                          </div>
                        </SpotIncentiveImagePreview>
                      );
                    }

                    // Fallback for edge cases
                    return (
                      <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-3 flex-1">
                        <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted flex-shrink-0">
                          <i className="fa fa-image text-lg text-muted-foreground"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {uploadedFileName || "Evidence image"}
                          </p>
                          <p className="text-xs text-muted-foreground">Ready for AI analysis</p>
                        </div>
                      </div>
                    );
                  })() : (
                    <Button
                      variant="outline"
                      className={cn(selectionTriggerBaseClass, "flex-1")}
                      onClick={() => uploadInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <span className="text-sm text-muted-foreground">
                        {isUploading ? "Uploading..." : "Upload image for AI assistance..."}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Upload</span>
                    </Button>
                  )}

                  {!fileId && uploadedDocumentId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-12 px-3 flex-shrink-0"
                      onClick={() => {
                        form.setValue("uploadedDocumentId", null);
                        form.setValue("uploadedFileName", undefined);
                        setUploadedFile(null);
                      }}
                    >
                      <i className="fa fa-xmark text-sm"></i>
                    </Button>
                  )}
                </div>

                <input
                  ref={uploadInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                    e.target.value = "";
                  }}
                />
              </FormSection>

              <FormSection
                step={4}
                title="Assign Points"
                description="Apply positive or negative tags to finalize the adjustment."
                completed={completionState.points}
                dimmed={isPointsAssignmentLocked}
                dependencyMessage="Select a task and at least one resource before assigning points."
                showDependencyMessage={isPointsAssignmentLocked}
                showPending
              >
                <div className={cn("rounded-lg border border-transparent", isPointsAssignmentLocked && "border-dashed border-border/70 bg-transparent")}>
                  {!hasPointsAssigned ? (
                    <Button
                      variant="outline"
                      className={cn(selectionTriggerBaseClass, "w-full justify-start py-3", isPointsAssignmentLocked && "cursor-not-allowed opacity-60")}
                      onClick={() => {
                        if (!isPointsAssignmentLocked) {
                          setShowPointsAssignment(true);
                        }
                      }}
                      disabled={isPointsAssignmentLocked}
                    >
                      <div className="flex-1 text-left">
                        <p className="text-sm font-semibold text-foreground">
                          0 points assigned
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          No tags assigned yet
                        </p>
                      </div>
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">Assign</span>
                    </Button>
                  ) : (
                    <div className="space-y-3 rounded-xl border border-border/60 bg-background p-4 shadow-inner">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 text-left">
                          <p className={cn("text-sm font-semibold", totalPoints < 0 ? "text-destructive" : totalPoints > 0 ? "text-green-600 dark:text-green-500" : "text-foreground")}>
                            {totalPoints > 0 ? "+" : ""}{totalPoints} points assigned
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {positiveTagIds.length > 0 && negativeTagIds.length > 0
                              ? `${positiveTagIds.length} positive, ${negativeTagIds.length} negative`
                              : positiveTagIds.length > 0
                                ? `${positiveTagIds.length} positive tag${positiveTagIds.length !== 1 ? 's' : ''}`
                                : `${negativeTagIds.length} negative tag${negativeTagIds.length !== 1 ? 's' : ''}`
                            }
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPointsAssignment(true)}
                          className="text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
                        >
                          Edit
                        </Button>
                      </div>

                      <div className="space-y-2 pt-2 border-t border-border/50">
                        {positiveTagIds.map((tagId: number) => {
                          const tag = project?.incentiveTagsStore?.getById(tagId);
                          const points = tag?.categoryRecord?.points || 0;
                          return (
                            <div key={`positive-${tagId}`} className="group flex items-center justify-between rounded-lg border border-green-600/20 dark:border-green-500/20 bg-green-600/5 dark:bg-green-500/10 px-3 py-2 relative">
                              <span className="text-sm font-medium text-foreground truncate pr-2">{tag?.name || `Tag ${tagId}`}</span>
                              <span className="text-sm font-semibold text-green-600 dark:text-green-500 flex-shrink-0">+{points} pts</span>
                              <button
                                onClick={() => {
                                  const updatedPositiveTags = positiveTagIds.filter((id: number) => id !== tagId);
                                  form.setValue("positiveTagIds", updatedPositiveTags, { shouldValidate: true });
                                }}
                                className="absolute -right-2 -top-2 bg-destructive rounded-full h-5 w-5 flex items-center justify-center hover:bg-destructive/90 transition-all opacity-0 group-hover:opacity-100"
                              >
                                <i className="fa fa-xmark text-[10px] text-destructive-foreground"></i>
                              </button>
                            </div>
                          );
                        })}
                        {negativeTagIds.map((tagId: number) => {
                          const tag = project?.incentiveTagsStore?.getById(tagId);
                          const points = tag?.categoryRecord?.points || 0;
                          return (
                            <div key={`negative-${tagId}`} className="group flex items-center justify-between rounded-lg border border-destructive/20 bg-destructive/5 dark:bg-destructive/10 px-3 py-2 relative">
                              <span className="text-sm font-medium text-foreground truncate pr-2">{tag?.name || `Tag ${tagId}`}</span>
                              <span className="text-sm font-semibold text-destructive flex-shrink-0">-{points} pts</span>
                              <button
                                onClick={() => {
                                  const updatedNegativeTags = negativeTagIds.filter((id: number) => id !== tagId);
                                  form.setValue("negativeTagIds", updatedNegativeTags, { shouldValidate: true });
                                }}
                                className="absolute -right-2 -top-2 bg-destructive rounded-full h-5 w-5 flex items-center justify-center hover:bg-destructive/90 transition-all opacity-0 group-hover:opacity-100"
                              >
                                <i className="fa fa-xmark text-[10px] text-destructive-foreground"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {formState.errors.positiveTagIds && !hasPointsAssigned && (
                  <p className="text-xs text-destructive">{String(formState.errors.positiveTagIds.message)}</p>
                )}
              </FormSection>
            </div>
          </div>
        </BaseModalBody>
      </BaseModal>

      {/* Modals */}
      <SpotIncentiveTaskSelector
        open={showTaskSelector}
        onOpenChange={setShowTaskSelector}
        selectedTaskId={taskId}
        onTaskSelect={(taskId) => {
          form.setValue("taskId", taskId);
          form.setValue("resourceIds", []);
          // Clear file-related fields when task changes
          // since the file is associated with the original task
          form.setValue("fileId", null);
          form.setValue("uploadedDocumentId", null);
          form.setValue("uploadedFileName", undefined);
          setUploadedFile(null);
        }}
        editorInstance={editorInstance}
      />

      <SpotIncentiveResourceSelector
        open={showResourceSelector}
        onOpenChange={setShowResourceSelector}
        selectedResourceIds={resourceIds}
        onResourcesSelect={(data) => {
          form.setValue("resourceIds", data.resourceIds);
          form.setValue("startDate", data.startDate);
          form.setValue("endDate", data.endDate);
          form.setValue("multiplyByDays", data.multiplyByDays);
        }}
        selectedTaskId={taskId}
        editorInstance={editorInstance}
        multiplyByDays={multiplyByDays}
      />

      <SpotIncentivePointsAssignment
        open={showPointsAssignment}
        onOpenChange={setShowPointsAssignment}
        pointsAssignment={{
          positiveTagIds,
          negativeTagIds
        }}
        onPointsChange={(points) => {
          form.setValue("positiveTagIds", points.positiveTagIds, { shouldValidate: true });
          form.setValue("negativeTagIds", points.negativeTagIds, { shouldValidate: true });
        }}
        documentId={effectiveDocumentId}
        editorInstance={editorInstance}
      />

      <Dialog open={showInactiveDialog} onOpenChange={setShowInactiveDialog}>
        <DialogContent closeButtonClassName="hidden" onInteractOutside={(e) => e.preventDefault()} className="z-[--swivu-bryntum-z-index] sm:max-w-lg" dialogOverlayClassName="z-[--swivu-bryntum-z-index]">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <Info className="h-6 w-6 text-primary" />
              <DialogTitle className="text-lg">Pending Points Alert</DialogTitle>
            </div>
            <DialogDescription className="text-sm leading-relaxed space-y-2">
              <p>
                To release pending points to your board users, click <strong>“Release Points”</strong> below.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={async () => {
                setShowInactiveDialog(false);
                // Update the created spot incentive status to RELEASED
                if (project?.spotIncentiveStore && createdSpotIncentiveId) {
                  const spotIncentive = project.spotIncentiveStore.getById(createdSpotIncentiveId);
                  if (spotIncentive) {
                    spotIncentive.isReleased = true;
                    showSuccessToast();
                  }
                }
                onClose();
              }}
              size="sm"
              className="w-full sm:w-auto"
            >
              Release Points
            </Button>
            <Button
              variant="default"
              onClick={() => {
                setShowInactiveDialog(false);
                if (onReviewClick) {
                  onReviewClick(uploadedFileRecord.current);
                }
                onClose();
              }}
              size="sm"
              className="w-full sm:w-auto"
            >
              Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SpotIncentive;
